/**
 * ImportStatusSection - Provider-agnostic import status section.
 *
 * Renders one row per active provider (Gmail, Test Email, Google Calendar,
 * future providers) using metadata supplied by the backend via
 * `useImportStatus`.  No provider names or icon paths are hard-coded here —
 * adding a new provider only requires extending the backend registry and the
 * frontend `providerDisplayRegistry`.
 */

import type {
  ImportStatus,
  ServiceImportStatus,
} from '@/hooks/integrations/useImportStatus';
import { buildImportCountLabel } from '@/hooks/integrations/useImportStatus';
import type { RecentImportItem } from '@/hooks/integrations/useRecentImportItems';
import { ProgressBarBase } from '@/lib/base/progress-indicators';
import { cx } from '@/utils/cx';

import { ActivityFeed } from './ActivityFeed';

interface ImportStatusSectionProps {
  /** Import status data from useImportStatus hook */
  importStatus: ImportStatus;
  /**
   * Live feed of just-imported emails/meetings, rendered as a rotating
   * one-line ticker under the provider rows. Pass
   * `useRecentImportItems().data` — hidden when absent or empty.
   */
  recentItems?: RecentImportItem[];
}

/** Pulsing activity indicator dot */
function ActivityIndicator({ active }: { active: boolean }) {
  return (
    <span
      className={cx(
        'size-2 rounded-full',
        active ? 'bg-utility-success-500 animate-pulse' : 'bg-utility-gray-300'
      )}
    />
  );
}

/** Individual import row - styled to match MeetingRow */
function ImportRow({
  icon,
  label,
  progress,
  indeterminate,
  countLabel,
  eta,
  active,
}: {
  icon: string | null;
  label: string;
  progress: number;
  indeterminate: boolean;
  countLabel: string | null;
  eta: string | null;
  active: boolean;
}) {
  return (
    <div
      className={cx(
        'relative overflow-hidden bg-bg-primary',
        'rounded-[4px] border border-secondary',
        'px-4 py-3'
      )}
    >
      {/* Header row: icon (or neutral placeholder), label, activity indicator, ETA */}
      <div className='flex items-center gap-3 mb-2'>
        {icon ? (
          <img src={icon} alt={label} className='h-5 w-5 shrink-0' />
        ) : (
          // Fallback placeholder when the provider has no registry icon. Mirror
          // the a11y treatment from OnboardingImportState (commit f467fa9754):
          // expose `role='img' aria-label={label}` instead of hiding the
          // element so screen-reader users get a named graphic for every
          // provider row, not silence-then-text.
          <span
            role='img'
            aria-label={label}
            className='h-5 w-5 shrink-0 rounded-[4px] bg-tertiary'
          />
        )}
        <span className='text-[14px] font-medium text-fg-primary flex-1'>
          {label}
        </span>
        {countLabel && (
          <span className='text-[13px] text-tertiary tabular-nums'>
            {countLabel}
          </span>
        )}
        {active && <ActivityIndicator active={true} />}
        {eta && (
          <span className='text-[13px] text-tertiary tabular-nums'>{eta}</span>
        )}
      </div>
      {/* Progress bar — indeterminate sweep when the provider reports no
          total estimate (a frozen 0% bar would misread as "stuck") */}
      <ProgressBarBase
        value={progress}
        min={0}
        max={100}
        indeterminate={indeterminate}
        progressClassName='bg-[var(--color-bg-brand-solid)]'
      />
    </div>
  );
}

/** A provider service that should appear in the section (has jobs and isn't fully complete). */
function shouldShowService(service: ServiceImportStatus): boolean {
  return service.hasJobs && service.progress < 100;
}

/**
 * ImportStatusSection - Shows import progress during active integration sync.
 *
 * Features:
 * - One progress row per active provider (Gmail, GCal, Test Email, etc.)
 * - Per-provider ETA display
 * - Activity indicators showing which providers are actively syncing
 * - Auto-hides when no active imports OR when every provider is fully complete
 */
export function ImportStatusSection({
  importStatus,
  recentItems,
}: ImportStatusSectionProps) {
  // Only show during active imports
  if (!importStatus.hasActiveImports) {
    return null;
  }

  const visibleServices = importStatus.services.filter(shouldShowService);

  // If every provider is complete (or none have jobs), hide the section.
  if (visibleServices.length === 0) {
    return null;
  }

  return (
    <div className='space-y-3'>
      <h2 className='text-[14px] font-semibold text-fg-primary'>
        Import status
      </h2>

      <div className='space-y-1.5'>
        {visibleServices.map(service => (
          <ImportRow
            key={service.providerName}
            icon={service.iconPath}
            label={service.displayName}
            progress={service.progress}
            indeterminate={service.indeterminate}
            countLabel={buildImportCountLabel(service)}
            eta={service.estimatedTimeRemaining}
            active={service.active}
          />
        ))}
      </div>

      {/* Live ticker of just-imported emails/meetings */}
      {recentItems && recentItems.length > 0 && (
        <ActivityFeed items={recentItems} />
      )}
    </div>
  );
}
